import { useEffect, useState } from "react";

import {
  DocumentList,
  DocumentsModal,
  Button,
} from "@king-ict/eupisi-ui/components";
import { UploadFile } from "@king-ict/eupisi-ui/models";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";

import { RegistrationEnrollmentFormSection } from "./components";
import { DEFAULT_REQUEST } from "./constants";
import styles from "./Request.module.scss";
import { getInstitutionById } from "../../api/Institution/InstitutionApi";
import {
  getRequest,
  getRegistrationForm,
  getRequestDocument,
  saveRegistrationForm,
  registrationFormCreatedRequest,
} from "../../api/Request/RequestApi";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  ToolbarTop,
  ToolbarEdit,
  FormCard,
  RequestHeader,
} from "../../components";
import { ConfirmModal } from "../../components/FormControls/ConfirmModal";
import { ValidationFailure } from "../../models";
import { Institution } from "../../models/Institution";
import {
  Request,
  RequestStatus,
  DocumentType,
  RegistrationForm,
  RegistrationFormStatus,
} from "../../models/Request";
import { Attachment } from "../../models/Shared/Attachment";
import { resetIsEdited, setIsEdited } from "../../slices/applicationSlice";
import {
  isSecondSignatureInProgressDisabled,
  isApproveOneSignatureDisabled,
  isApproveTwoSignaturesDisabled,
  isApproveAllSignaturesDisabled,
} from "../../utils/buttonStatusChecks"; // Sve provjere za vrijednost `disabled` propsa na botunima mogu se staviti u buttonStatusChecks.ts

export function RegistrationFormEdit() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();

  const createDefaultForm = (): RegistrationForm => {
    return {
      id: 0,
      requestId: +id,
      status: {
        id: RegistrationFormStatus.FirstSignatureInProgress,
      },
    };
  };
  const [item, setItem] = useState<Request>(DEFAULT_REQUEST);
  const [registrationForm, setRegistrationForm] = useState<RegistrationForm>(
    createDefaultForm()
  );
  const [primaryInstitution, setPrimaryInstitution] = useState<
    Institution | undefined
  >();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [confirmState, setConfirmState] = useState<any>();
  const [confirmMessage, setConfirmMessage] = useState<any>();
  const [confirmRegistrationFormStatus, setConfirmRegistrationFormStatus] =
    useState<RegistrationFormStatus | undefined>();
  const [errors, setErrors] = useState<ValidationFailure[]>();
  const [documentsEdited, setDocumentsEdited] = useState(false);

  const fetchRegistrationForm = async (id: string) => {
    if (!id) return;

    const result = await getRegistrationForm(+id);

    if (result) {
      setRegistrationForm(result);
      if (result.request) {
        setItem(result.request);
      }
    } else {
      const request = await getRequest(+id);
      if (request) {
        setItem(request);
      }
    }

    dispatch(resetIsEdited());
  };

  useEffect(() => {
    fetchRegistrationForm(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  useEffect(() => {
    (async () => {
      if (
        item.institutionId &&
        item.schoolYearId > 0 &&
        (!primaryInstitution || primaryInstitution.id !== item.institutionId)
      ) {
        setPrimaryInstitution(
          await getInstitutionById(item.institutionId, item.schoolYearId)
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.institutionId, item.schoolYearId]);

  const handleStatusChange = async (status: RegistrationFormStatus) => {
    setConfirmRegistrationFormStatus(status);

    switch (status) {
      case RegistrationFormStatus.OneSignature:
        setConfirmMessage(t("registrationForm.approveOneSignatureConfirm"));
        break;
      case RegistrationFormStatus.SecondSignatureInProgress:
        setConfirmMessage(t("registrationForm.sendToSecondParentConfirm"));
        break;
      case RegistrationFormStatus.TwoSignatures:
        setConfirmMessage(t("registrationForm.approveTwoSignaturesConfirm"));
        break;
      case RegistrationFormStatus.AllSignatures:
        setConfirmMessage(t("registrationForm.approvedAllSignaturesConfirm"));
        break;
    }

    setConfirmState("confirm");
  };
  const handleConfirmClicked = async () => {
    const newStatus = confirmRegistrationFormStatus!;
    handleConfirmCancel();
    if (newStatus !== RegistrationFormStatus.FirstSignatureInProgress) {
      await doStatusChange(newStatus);
    } else {
      await doRequestStatusChange();
    }
    await fetchRegistrationForm(id);
  };
  const handleConfirmCancel = () => {
    setConfirmMessage(undefined);
    setConfirmState(undefined);
    setConfirmRegistrationFormStatus(undefined);
  };
  const handleRequestStatusChange = async () => {
    // HACK! We don't want to deal with many, many variables. Use this default status.
    setConfirmRegistrationFormStatus(
      RegistrationFormStatus.FirstSignatureInProgress
    );
    setConfirmMessage(t("registrationForm.registrationFormCreatedConfirm"));
    setConfirmState("confirm");
  };
  const handleDocumentDownload = async (
    doc: Attachment,
    type: DocumentType
  ) => {
    if (doc && doc.id) {
      await getRequestDocument(doc.id, item.id, type);
    }
  };
  /**
   * Reads the file binary data and pushes the listed files to the uploadDocument state variable.
   * @param files
   */
  const handleFileChange = async (files: File[]) => {
    const uploadedFiles: UploadFile[] = [];
    if (files && files.length > 0) {
      files.forEach((file) => {
        const fileReader = new FileReader();
        fileReader.readAsBinaryString(file);
        const uploadedFile = {
          fileName: file.name,
          fileData: fileReader.result,
        } as UploadFile;
        uploadedFiles.push(uploadedFile);
      });
    }
    const newRegistrationForm: RegistrationForm = {
      ...registrationForm,
      uploadFiles: uploadedFiles.length > 0 ? uploadedFiles : [],
    };
    setRegistrationForm(newRegistrationForm);
    setDocumentsEdited(true);
    dispatch(setIsEdited());
  };
  /**
   * Pushes the listed files to the uploadDocument state variable, sets
   * the appropriate collection on the item, and closes the modal.
   * @param files
   */
  const handleUploadFileChange = (files: UploadFile[]) => {
    const combined = (files ?? []).concat(registrationForm.uploadFiles ?? []);
    setShowUploadModal(false);
    const newRegistrationForm: RegistrationForm = {
      ...registrationForm,
      uploadFiles: combined.length > 0 ? combined : [],
    };
    setRegistrationForm(newRegistrationForm);
    setDocumentsEdited(true);
    dispatch(setIsEdited());
  };
  /**
   * Removes the specified doc (existing attachment) from the signed document collection.
   * @param doc
   */
  const handleRemoveAttachment = (doc: Attachment) => {
    if (doc && doc.id) {
      const newRegistrationForm: RegistrationForm = {
        ...registrationForm,
        signedAttachments: registrationForm.signedAttachments?.filter(
          (v) => v.id !== doc.id
        ),
      };
      setRegistrationForm(newRegistrationForm);
      setDocumentsEdited(true);
      dispatch(setIsEdited());
    }
  };
  const doStatusChange = async (status: RegistrationFormStatus) => {
    const newRegistrationForm: RegistrationForm = {
      ...registrationForm,
      status: {
        id: status,
      },
    };
    const result = await saveRegistrationForm(newRegistrationForm);
    if (result) {
      setErrors(result.validationFailures);
      if (result.isValid && result.result) {
        setRegistrationForm(result.result);
        setDocumentsEdited(false);
        dispatch(resetIsEdited());
        if (status === RegistrationFormStatus.AllSignatures) history.goBack();
      }
    }
  };
  const doRequestStatusChange = async () => {
    const result = await registrationFormCreatedRequest(item.id);
    if (result) {
      setErrors(result.validationFailures);
      if (result.isValid && result.result) {
        setRegistrationForm(result.result);
        if (result.result.request) {
          setItem(result.result.request);
        }
        setDocumentsEdited(false);
        dispatch(resetIsEdited());
      }
    }
  };

  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"hide"}
        showBack={"show"}
        pageTitle={t("registrationForm.pageTitle")}
        errors={errors}
      />
      <RequestHeader request={item} />
      <FormCard cardTitle={t("registrationForm.pageTitle")}>
        <div style={{ display: "flex", gap: "1rem" }}>
          <DocumentList
            documents={
              registrationForm.attachment ? [registrationForm.attachment] : []
            }
            onDocumentClick={(doc) =>
              handleDocumentDownload(doc, DocumentType.RegistrationForm)
            }
            removable={false}
          />
          <RegistrationEnrollmentFormSection
            requestId={item.id}
            registrationFormDataChangeId={item.registrationFormDataChangeId}
          />
        </div>
      </FormCard>
      <FormCard cardTitle={t("registrationForm.registrationFormSigned")}>
        <Row>
          <Col md={4}>
            <DocumentList
              documents={registrationForm.signedAttachments ?? []}
              onDocumentClick={(doc) =>
                handleDocumentDownload(doc, DocumentType.RegistrationForm)
              }
              onRemoveClick={(doc) => handleRemoveAttachment(doc)}
              removable={
                item.statusId === RequestStatus.RegistrationFormCreated
              }
            />
          </Col>
          <Col md={4}>
            <Button
              color="primary"
              onClick={() => setShowUploadModal(true)}
              size="sm"
              disabled={item.statusId !== RequestStatus.RegistrationFormCreated}
            >
              {t("request.documentUpload")}
            </Button>
            <br />
            <br />
            <Button
              color="primary"
              onClick={() =>
                doStatusChange(
                  registrationForm.status?.id ??
                    RegistrationFormStatus.FirstSignatureInProgress
                )
              }
              size="sm"
              disabled={
                item.statusId !== RequestStatus.RegistrationFormCreated ||
                !documentsEdited
              }
            >
              {t("common.save")}
            </Button>
          </Col>
          <Col md={4}>
            <DocumentList
              documents={registrationForm.uploadFiles ?? []}
              removable={false}
            />
          </Col>
        </Row>
      </FormCard>
      <FormCard>
        <Row>
          <Col md={12}>
            <Button
              className="mr-2"
              size="sm"
              onClick={() => {
                if (
                  registrationForm.status?.id ===
                  RegistrationFormStatus.OneSignatureDigitalConfirmation
                ) {
                  handleStatusChange(
                    RegistrationFormStatus.OneSignatureDigitalConfirmation
                  );
                } else {
                  handleStatusChange(RegistrationFormStatus.OneSignature);
                }
              }}
              disabled={isApproveOneSignatureDisabled(registrationForm, item)}
            >
              {t("registrationForm.approveOneSignature")}
            </Button>
            <Button
              className="mr-2"
              size="sm"
              onClick={() =>
                handleStatusChange(RegistrationFormStatus.TwoSignatures)
              }
              disabled={isApproveTwoSignaturesDisabled(registrationForm, item)}
            >
              {t("registrationForm.approveTwoSignatures")}
            </Button>
            <Button
              className="mr-2"
              size="sm"
              onClick={() =>
                handleStatusChange(
                  RegistrationFormStatus.SecondSignatureInProgress
                )
              }
              disabled={isSecondSignatureInProgressDisabled(
                registrationForm,
                item
              )}
            >
              {t("registrationForm.sendToSecondParent")}
            </Button>
            <Button
              className="mr-2"
              size="sm"
              onClick={() =>
                handleStatusChange(RegistrationFormStatus.AllSignatures)
              }
              disabled={isApproveAllSignaturesDisabled(registrationForm, item)}
            >
              {t("registrationForm.approvedAllSignatures")}
            </Button>
          </Col>
        </Row>
        {item.statusId === RequestStatus.Registered && (
          <Row className={styles.rowPaddingMedium}>
            <Col md={12}>
              <Button
                className="mr-2"
                size="sm"
                color="danger"
                onClick={handleRequestStatusChange}
              >
                {t("registrationForm.registrationFormCreated")}
              </Button>
            </Col>
          </Row>
        )}
      </FormCard>
      <ToolbarEdit
        showCancel={"show"}
        showSave={"hide"}
        setSaveErrors={setErrors}
        showCustom={true}
      />
      <DocumentsModal
        title={t("accessibility.documentModal.title")}
        infoPanelText={t("accessibility.documentModal.info")}
        types={t("accessibility.documentModal.types")}
        allDocuments={registrationForm.uploadFiles ?? []}
        maxSize={10}
        filesAction={handleFileChange}
        open={showUploadModal}
        onConfirm={handleUploadFileChange}
        onClose={() => {
          setShowUploadModal(false);
        }}
        hideOldDocuments={false}
        btnCancelText={t("common.quit")}
        btnOkText={t("common.addNew")}
      />
      <ConfirmModal
        title={t("common.requestModel.enrollmentConfirm")}
        confirmState={confirmState}
        confirmMessage={confirmMessage}
        successMesage={t("common.requestModel.successMesage")}
        errorMessage={t("common.requestModel.errorMessage")}
        onClickClose={handleConfirmCancel}
        onClickConfirm={handleConfirmClicked}
      />
    </ContentArea>
  );
}
